import { Quill, loadQuill } from '@vueup/vue-quill'
import { adminService } from '../services/admin'

export const DEFAULT_FONT_FAMILY = 'Manrope, sans-serif'
export const DEFAULT_FONT_LABEL = 'Manrope'
export const DEFAULT_FONT_SIZE = '18px'
export const BLANK_QUILL_HTML = `<p style="font-family: ${DEFAULT_FONT_FAMILY}; font-size: ${DEFAULT_FONT_SIZE};"><br></p>`

export const QUILL_FONT_SIZE_OPTIONS = [
  DEFAULT_FONT_SIZE,
  '20px',
  '24px',
  '28px',
  '32px'
]

export const QUILL_FONT_FAMILY_OPTIONS = [
  DEFAULT_FONT_FAMILY,
  'Lora, serif',
  'EB Garamond, serif',
  'Georgia, serif',
  'Times New Roman, serif'
]

const SIZE_WHITELIST = [...QUILL_FONT_SIZE_OPTIONS]
const FONT_WHITELIST = [...QUILL_FONT_FAMILY_OPTIONS]
const HEADER_OPTIONS = [false, 1, 2, 3]
const DEFAULT_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'align',
  'list',
  'bullet',
  'blockquote',
  'code-block',
  'link',
  'image',
  'video'
]
// vue-quill 1.5.x lazy-loads Quill (loadQuill()), so the Quill class is NOT
// available at module-evaluation time — calling Quill.import(...) eagerly throws
// "Quill is not loaded yet". Defer all Quill.import/register and the custom
// module class (which extends a Quill base class) until after loadQuill()
// resolves. RichTextEditor awaits registerQuillFormats() before mounting the
// editor, so formats/modules are registered before any Quill instance is created.
let quillFormatsRegistered = false

export async function registerQuillFormats () {
  if (quillFormatsRegistered) return
  await loadQuill()

  const Size = Quill.import('attributors/style/size')
  Size.whitelist = SIZE_WHITELIST
  Quill.register(Size, true)

  const Font = Quill.import('attributors/style/font')
  Font.whitelist = FONT_WHITELIST
  Quill.register(Font, true)

  const BaseModule = Quill.import('core/module')

  class DefaultFormatsModule extends BaseModule {
    constructor (quill, options = {}) {
      super(quill, options)
      this.options = {
        font: DEFAULT_FONT_FAMILY,
        size: DEFAULT_FONT_SIZE,
        ...options
      }
      this.applyDefaults()
    }

    applyDefaults () {
      const { options } = this
      const quill = this.quill
      if (!quill) return

      const length = quill.getLength()
      if (length > 1) {
        const formats = quill.getFormat(0, length - 1)
        const hasFont = Boolean(formats.font)
        const hasSize = Boolean(formats.size)
        if (!hasFont && options.font) {
          quill.format('font', options.font)
        }
        if (!hasSize && options.size) {
          quill.format('size', options.size)
        }
        return
      }

      if (options.font) {
        quill.format('font', options.font)
      }
      if (options.size) {
        quill.format('size', options.size)
      }
    }
  }

  Quill.register('modules/defaultFormats', DefaultFormatsModule, true)
  quillFormatsRegistered = true
}

const FONT_CLASS_MAP = {
  'ql-font-manrope': "'Manrope', sans-serif",
  'ql-font-lora': "'Lora', serif",
  'ql-font-eb-garamond': "'EB Garamond', serif",
  'ql-font-georgia': "'Georgia', serif",
  'ql-font-times-new-roman': "'Times New Roman', serif"
}

const SIZE_CLASS_MAP = {
  'ql-size-12px': '12px',
  'ql-size-14px': '14px',
  'ql-size-16px': '16px',
  'ql-size-18px': '18px',
  'ql-size-20px': '20px',
  'ql-size-24px': '24px',
  'ql-size-28px': '28px',
  'ql-size-32px': '32px'
}

const isDev =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV !== 'production'

const IMAGE_ACCEPT = 'image/*,.heic,.heif'
const DOCUMENT_ACCEPT =
  '.pdf,.doc,.docx,.txt,.rtf,.odt,.pages,.xls,.xlsx,.ppt,.pptx'
const IMAGE_MAX_BYTES = 10 * 1024 * 1024
const DOCUMENT_MAX_BYTES = 500 * 1024 * 1024

const DEFAULT_TOOLBAR_TEMPLATE = [
  [
    { font: QUILL_FONT_FAMILY_OPTIONS },
    { size: QUILL_FONT_SIZE_OPTIONS },
    { header: HEADER_OPTIONS }
  ],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video', 'attachment'],
  ['clean']
]

const DOCUMENT_EXTENSIONS = DOCUMENT_ACCEPT.replace(/\s/g, '')
  .split(',')
  .filter(Boolean)

const HEIC_EXTENSIONS = ['.heic', '.heif']

const errorMessages = {
  imageType: 'Please select a valid image file',
  imageSize: 'Image size must be less than 10MB',
  documentType:
    'Please select a document file (PDF, DOC, DOCX, TXT, RTF, ODT, PAGES, XLS, XLSX, PPT, or PPTX)',
  documentSize: 'Document size must be less than 500MB'
}

function escapeHtml (value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function sanitizeUrl (url) {
  return url.replace(/"/g, '%22')
}

function buildToolbar ({ allowDocuments, allowImages, allowVideos, toolbar }) {
  if (Array.isArray(toolbar) && toolbar.length > 0) {
    return toolbar
  }

  return DEFAULT_TOOLBAR_TEMPLATE.map(group => {
    if (Array.isArray(group)) {
      const filtered = group.filter(item => {
        if (item === 'image') {
          return allowImages
        }
        if (item === 'video') {
          return allowVideos
        }
        if (item === 'attachment') {
          return allowDocuments
        }
        return true
      })
      return filtered
    }

    if (typeof group === 'object' && group !== null) {
      return { ...group }
    }

    return group
  }).filter(group => {
    if (Array.isArray(group)) {
      return group.length > 0
    }
    return true
  })
}

export function convertLegacyQuillContent (html) {
  if (!html || typeof html !== 'string') {
    return html || ''
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return html
  }

  const container = document.createElement('div')
  container.innerHTML = html

  const fontSelectors = Object.keys(FONT_CLASS_MAP)
  const sizeSelectors = Object.keys(SIZE_CLASS_MAP)

  if (fontSelectors.length > 0) {
    container
      .querySelectorAll(fontSelectors.map(cls => `.${cls}`).join(','))
      .forEach(node => {
        const matchingClass = Array.from(node.classList).find(
          cls => FONT_CLASS_MAP[cls]
        )
        if (matchingClass) {
          if (!node.style.fontFamily) {
            node.style.fontFamily = FONT_CLASS_MAP[matchingClass]
          }
          node.classList.remove(matchingClass)
          if (node.classList.length === 0) {
            node.removeAttribute('class')
          }
        }
      })
  }

  if (sizeSelectors.length > 0) {
    container
      .querySelectorAll(sizeSelectors.map(cls => `.${cls}`).join(','))
      .forEach(node => {
        const matchingClass = Array.from(node.classList).find(
          cls => SIZE_CLASS_MAP[cls]
        )
        if (matchingClass) {
          if (!node.style.fontSize) {
            node.style.fontSize = SIZE_CLASS_MAP[matchingClass]
          }
          node.classList.remove(matchingClass)
          if (node.classList.length === 0) {
            node.removeAttribute('class')
          }
        }
      })
  }

  container.querySelectorAll('p, li, blockquote, div, span').forEach(node => {
    if (!node || !(node instanceof HTMLElement)) {
      return
    }

    if (!node.style) {
      return
    }

    if (!node.style.fontFamily) {
      node.style.fontFamily = DEFAULT_FONT_FAMILY
    }

    if (!node.style.fontSize && node.tagName !== 'SPAN') {
      node.style.fontSize = DEFAULT_FONT_SIZE
    }
  })

  if (!container.innerHTML.trim()) {
    container.innerHTML = BLANK_QUILL_HTML
  }

  return container.innerHTML
}

export function isQuillContentEmpty (html) {
  if (!html || typeof html !== 'string') {
    return true
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    const stripped = html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, '')
      .trim()
    return stripped.length === 0
  }

  const container = document.createElement('div')
  container.innerHTML = convertLegacyQuillContent(html)

  const mediaSelector =
    'img, video, iframe, embed, object, table, ul li, ol li, blockquote, pre, code, a[data-attachment]'
  if (container.querySelector(mediaSelector)) {
    return false
  }

  const textContent = container.textContent?.replace(/\u200B/g, '').trim()
  if (textContent) {
    return false
  }

  const residual = container.innerHTML
    .replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?|&#8203;)*<\/p>/gi, '')
    .replace(/<br\s*\/?>(\s|&nbsp;)*/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s/g, '')

  return residual.length === 0
}

function createFileInput (accept) {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', accept)
  input.style.display = 'none'
  document.body.appendChild(input)
  return input
}

function pickSingleFile ({ accept, validateFile }) {
  return new Promise((resolve, reject) => {
    const input = createFileInput(accept)
    input.click()

    input.onchange = () => {
      const file = input.files && input.files[0]
      input.remove()

      if (!file) {
        reject(new Error('No file selected'))
        return
      }

      const validationError = validateFile ? validateFile(file) : null
      if (validationError) {
        alert(validationError)
        reject(new Error(validationError))
        return
      }

      resolve(file)
    }

    input.oncancel = () => {
      input.remove()
      reject(new Error('File selection cancelled'))
    }
  })
}

function resolveQuill (getEditor) {
  if (!getEditor) {
    return null
  }

  const editorInstance =
    typeof getEditor === 'function' ? getEditor() : getEditor

  if (editorInstance && typeof editorInstance.getQuill === 'function') {
    return editorInstance.getQuill()
  }

  if (
    editorInstance &&
    editorInstance.value &&
    typeof editorInstance.value.getQuill === 'function'
  ) {
    return editorInstance.value.getQuill()
  }

  return null
}

function ensureRange (quill) {
  if (!quill) {
    return null
  }

  let range = quill.getSelection(true)
  if (!range) {
    const length = quill.getLength()
    range = { index: length, length: 0 }
    quill.setSelection(length, 0)
  }
  return range
}

async function handleImageUpload (getEditor) {
  try {
    const file = await pickSingleFile({
      accept: IMAGE_ACCEPT,
      validateFile: selected => {
        const isImageType =
          selected.type.startsWith('image/') ||
          HEIC_EXTENSIONS.some(ext => selected.name.toLowerCase().endsWith(ext))
        if (!isImageType) {
          return errorMessages.imageType
        }

        if (selected.size > IMAGE_MAX_BYTES) {
          return errorMessages.imageSize
        }

        return null
      }
    })

    const { convertHeicForUpload } = await import('./heicConverter')
    const fileToUpload = await convertHeicForUpload(file)

    const formData = new FormData()
    formData.append('file', fileToUpload)

    const url = await adminService.uploadImage(formData)
    const quill = resolveQuill(getEditor)
    if (!quill || !url) {
      return
    }

    const range = ensureRange(quill)
    if (!range) {
      return
    }

    quill.focus()
    quill.insertEmbed(range.index, 'image', url, 'user')
    quill.setSelection(range.index + 1, 0, 'user')
  } catch (error) {
    if (isDev) {
      console.error('Image upload failed:', error)
    }
    if (error?.message && !error.message.includes('cancelled')) {
      alert('Failed to upload image. Please try again.')
    }
  }
}

function buildAttachmentHtml (url, filename) {
  const safeUrl = sanitizeUrl(url)
  const safeName = escapeHtml(filename || 'Download file')
  return `<a href="${safeUrl}" class="ql-attachment-link" target="_blank" rel="noopener noreferrer" data-attachment="true">${safeName}</a>`
}

async function handleDocumentUpload (getEditor) {
  try {
    const file = await pickSingleFile({
      accept: DOCUMENT_ACCEPT,
      validateFile: selected => {
        const lowerName = selected.name.toLowerCase()
        const hasAllowedExtension = DOCUMENT_EXTENSIONS.some(ext =>
          lowerName.endsWith(ext)
        )

        if (!hasAllowedExtension) {
          return errorMessages.documentType
        }

        if (selected.size > DOCUMENT_MAX_BYTES) {
          return errorMessages.documentSize
        }

        return null
      }
    })

    const formData = new FormData()
    formData.append('file', file)

    const url = await adminService.uploadEbook(formData)
    const quill = resolveQuill(getEditor)
    if (!quill || !url) {
      return
    }

    const range = ensureRange(quill)
    if (!range) {
      return
    }

    const attachmentHtml = buildAttachmentHtml(url, file.name)
    quill.focus()
    quill.clipboard.dangerouslyPasteHTML(range.index, attachmentHtml, 'user')
    const newIndex = range.index + file.name.length
    quill.insertText(newIndex, '\n', 'user')
    quill.setSelection(newIndex + 1, 0, 'user')
  } catch (error) {
    if (isDev) {
      console.error('Document upload failed:', error)
    }
    if (error?.message && !error.message.includes('cancelled')) {
      alert('Failed to upload document. Please try again.')
    }
  }
}

function handleLinkPrompt (getEditor, value) {
  const quill = resolveQuill(getEditor)
  if (!quill) {
    return
  }

  const range = ensureRange(quill)
  if (!range) {
    return
  }

  const href = window.prompt('Enter the URL:', value || '')
  if (!href) {
    return
  }

  quill.focus()
  if (range.length > 0) {
    quill.formatText(range.index, range.length, 'link', href, 'user')
  } else {
    quill.insertText(range.index, href, 'link', href, 'user')
    quill.setSelection(range.index + href.length, 0, 'user')
  }
}

function normalizeYouTube (url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  const videoId = match && match[2].length === 11 ? match[2] : null
  if (!videoId) {
    return null
  }
  return `https://www.youtube.com/embed/${videoId}`
}

function normalizeVimeo (url) {
  const regExp = /vimeo.com\/(\d+)/
  const match = url.match(regExp)
  const videoId = match ? match[1] : null
  if (!videoId) {
    return null
  }
  return `https://player.vimeo.com/video/${videoId}`
}

function handleVideoPrompt (getEditor) {
  const quill = resolveQuill(getEditor)
  if (!quill) {
    return
  }

  const range = ensureRange(quill)
  if (!range) {
    return
  }

  const url = window.prompt(
    'Enter video URL (YouTube, Vimeo, or direct video link):'
  )
  if (!url) {
    return
  }

  let embedUrl = url
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    embedUrl = normalizeYouTube(url)
    if (!embedUrl) {
      alert('Invalid YouTube URL')
      return
    }
  } else if (url.includes('vimeo.com')) {
    embedUrl = normalizeVimeo(url)
    if (!embedUrl) {
      alert('Invalid Vimeo URL')
      return
    }
  }

  quill.focus()
  quill.insertEmbed(range.index, 'video', embedUrl, 'user')
  quill.setSelection(range.index + 1, 0, 'user')
}

export function createQuillOptions ({
  getEditor,
  placeholder = 'Start writing...',
  defaultFont = DEFAULT_FONT_FAMILY,
  defaultSize = DEFAULT_FONT_SIZE
} = {}) {
  const allowImages = true
  const allowVideos = true
  const allowDocuments = true

  return {
    theme: 'snow',
    modules: {
      toolbar: {
        container: buildToolbar({
          allowDocuments,
          allowImages,
          allowVideos
        }),
        handlers: {
          image: () => handleImageUpload(getEditor),
          video: () => handleVideoPrompt(getEditor),
          link: value => handleLinkPrompt(getEditor, value),
          attachment: () => handleDocumentUpload(getEditor)
        }
      },
      defaultFormats: {
        font: defaultFont,
        size: defaultSize
      }
    },
    placeholder,
    formats: DEFAULT_FORMATS
  }
}

export function createCustomQuillOptions ({
  getEditor,
  placeholder = 'Start writing...',
  allowImages = true,
  allowVideos = true,
  allowDocuments = true,
  toolbar,
  formats = DEFAULT_FORMATS,
  defaultFont = DEFAULT_FONT_FAMILY,
  defaultSize = DEFAULT_FONT_SIZE
} = {}) {
  return {
    theme: 'snow',
    modules: {
      toolbar: {
        container: buildToolbar({
          allowDocuments,
          allowImages,
          allowVideos,
          toolbar
        }),
        handlers: {
          ...(allowImages ? { image: () => handleImageUpload(getEditor) } : {}),
          ...(allowVideos ? { video: () => handleVideoPrompt(getEditor) } : {}),
          link: value => handleLinkPrompt(getEditor, value),
          ...(allowDocuments
            ? { attachment: () => handleDocumentUpload(getEditor) }
            : {})
        }
      },
      defaultFormats: {
        font: defaultFont,
        size: defaultSize
      }
    },
    placeholder,
    formats
  }
}
